import { useState, useEffect } from 'react';
import { Wallet, Download, Trash2 } from 'lucide-react';
import { PieChart, Pie, Cell, Legend } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import ToolPage from '@/components/ToolPage';
import { toolCardClass } from '@/lib/toolStyles';

interface BudgetItem {
  id: string;
  name: string;
  amount: number;
  category: 'income' | 'expense';
  type: string;
}

interface BudgetCategory {
  id: string;
  name: string;
  type: 'income' | 'expense';
  icon: React.ElementType;
  description: string;
}

const incomeCategories: BudgetCategory[] = [
  { id: 'salary', name: 'Salary', type: 'income', icon: Wallet, description: 'Regular employment income' },
  { id: 'investments', name: 'Investments', type: 'income', icon: Wallet, description: 'Investment returns' },
  { id: 'other_income', name: 'Other Income', type: 'income', icon: Wallet, description: 'Additional income sources' }
];

const expenseCategories: BudgetCategory[] = [
  { id: 'housing', name: 'Housing', type: 'expense', icon: Wallet, description: 'Rent, mortgage, utilities' },
  { id: 'transportation', name: 'Transportation', type: 'expense', icon: Wallet, description: 'Car, fuel, public transit' },
  { id: 'food', name: 'Food', type: 'expense', icon: Wallet, description: 'Groceries and dining' },
  { id: 'utilities', name: 'Utilities', type: 'expense', icon: Wallet, description: 'Electricity, water, gas' },
  { id: 'insurance', name: 'Insurance', type: 'expense', icon: Wallet, description: 'Health, car, home insurance' },
  { id: 'entertainment', name: 'Entertainment', type: 'expense', icon: Wallet, description: 'Recreation and leisure' },
  { id: 'savings', name: 'Savings', type: 'expense', icon: Wallet, description: 'Emergency fund, investments' },
  { id: 'other', name: 'Other', type: 'expense', icon: Wallet, description: 'Miscellaneous expenses' }
];

const COLORS = ['#3b82f6', '#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const BUDGET_STORAGE_KEY = 'hoito-budget-planner-items';

function BudgetPlanner() {
  const [items, setItems] = useState<BudgetItem[]>(() => {
    try {
      const stored = localStorage.getItem(BUDGET_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [newItemCategory, setNewItemCategory] = useState<BudgetCategory | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [activeTab, setActiveTab] = useState<'income' | 'expense'>('income');
  const [showInfo, setShowInfo] = useState(false);

  const handleAddItem = () => {
    if (!newItemCategory || !newItemName || !newItemAmount) return;

    const newItem: BudgetItem = {
      id: `item-${Date.now()}`,
      name: newItemName,
      amount: Number(newItemAmount),
      category: newItemCategory.type,
      type: newItemCategory.id
    };

    setItems([...items, newItem]);
    setNewItemName('');
    setNewItemAmount('');
    setNewItemCategory(null);
  };

  useEffect(() => {
    localStorage.setItem(BUDGET_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const handleDeleteItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const getTotalIncome = () => items
    .filter(item => item.category === 'income')
    .reduce((sum, item) => sum + item.amount, 0);

  const getTotalExpenses = () => items
    .filter(item => item.category === 'expense')
    .reduce((sum, item) => sum + item.amount, 0);

  const getNetIncome = () => getTotalIncome() - getTotalExpenses();

  const getSavingsRate = () => {
    const totalIncome = getTotalIncome();
    return totalIncome > 0 ? ((totalIncome - getTotalExpenses()) / totalIncome) * 100 : 0;
  };

  const getCategoryData = (type: 'income' | 'expense') => {
    const categoryTotals: { [key: string]: number } = {};
    
    items.filter(item => item.category === type).forEach(item => {
      categoryTotals[item.type] = (categoryTotals[item.type] || 0) + item.amount;
    });

    return Object.entries(categoryTotals).map(([type, amount]) => ({
      name: (type === 'income' ? incomeCategories : expenseCategories)
        .find(cat => cat.id === type)?.name || type,
      value: amount
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadBudget = () => {
    let csv = 'Category,Type,Name,Amount\n';
    
    items.forEach(item => {
      csv += `${item.category},${item.type},${item.name},${item.amount}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'budget.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const budgetChartConfig = Object.fromEntries(
    (activeTab === 'income' ? incomeCategories : expenseCategories).map((c, i) => [
      c.name,
      { label: c.name, color: COLORS[i % COLORS.length] }
    ])
  );

  return (
    <ToolPage title="Budget Planner" description="Plan and track your monthly income and expenses" icon={Wallet}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className={toolCardClass}>
              <CardContent className="pt-6">
                <div className="flex gap-4 mb-6">
                  <Button variant={activeTab === 'income' ? 'default' : 'outline'} onClick={() => setActiveTab('income')}>
                    Income
                  </Button>
                  <Button variant={activeTab === 'expense' ? 'default' : 'outline'} onClick={() => setActiveTab('expense')}>
                    Expenses
                  </Button>
                </div>

                {!newItemCategory && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(activeTab === 'income' ? incomeCategories : expenseCategories).map((category) => (
                      <Button
                        key={category.id}
                        variant="outline"
                        className="h-auto py-4 px-4 justify-start gap-4"
                        onClick={() => setNewItemCategory(category)}
                      >
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                          <category.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="font-medium">{category.name}</h3>
                          <p className="text-sm text-muted-foreground">{category.description}</p>
                        </div>
                      </Button>
                    ))}
                  </div>
                )}

                {newItemCategory && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="item-name">Item Name</Label>
                      <Input id="item-name" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} placeholder="Enter item name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item-amount">Amount</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                        <Input id="item-amount" type="number" min={0} value={newItemAmount} onChange={(e) => setNewItemAmount(e.target.value)} placeholder="Enter amount" className="pl-8" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="ghost" onClick={() => setNewItemCategory(null)}>Cancel</Button>
                      <Button onClick={handleAddItem}>Add Item</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={toolCardClass}>
              <CardHeader className="flex flex-row justify-between items-center">
                <h2 className="text-lg font-semibold">{activeTab === 'income' ? 'Income Sources' : 'Expenses'}</h2>
                <Button variant="secondary" size="sm" onClick={downloadBudget}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Budget
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {items
                    .filter(item => item.category === activeTab)
                    .map(item => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg group">
                        <div>
                          <h3 className="font-medium">{item.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {(activeTab === 'income' ? incomeCategories : expenseCategories).find(cat => cat.id === item.type)?.name}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`font-medium ${item.category === 'income' ? 'text-brand-700' : 'text-red-600'}`}>
                            {formatCurrency(item.amount)}
                          </span>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" onClick={() => handleDeleteItem(item.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                  {items.filter(item => item.category === activeTab).length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">No items added yet</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className={toolCardClass}>
              <CardHeader>
                <h2 className="text-lg font-semibold">Budget Summary</h2>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Income</p>
                  <p className="text-2xl font-bold text-brand-700">{formatCurrency(getTotalIncome())}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(getTotalExpenses())}</p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-1">Net Income</p>
                  <p className={`text-2xl font-bold ${getNetIncome() >= 0 ? 'text-brand-700' : 'text-red-600'}`}>
                    {formatCurrency(getNetIncome())}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Savings Rate</p>
                  <p className="text-2xl font-bold text-primary">{getSavingsRate().toFixed(1)}%</p>
                </div>
              </CardContent>
            </Card>

            {items.length > 0 && (
              <Card>
                <CardHeader>
                  <h2 className="text-lg font-semibold">{activeTab === 'income' ? 'Income' : 'Expense'} Breakdown</h2>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={budgetChartConfig} className="h-[300px] w-full">
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value))} />} />
                      <Pie
                        data={getCategoryData(activeTab)}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {getCategoryData(activeTab).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <ChartLegend content={<ChartLegendContent />} />
                    </PieChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
    </ToolPage>
  );
}

export default BudgetPlanner;